import { useEffect } from "react";

import { HTTPStatus, type ResponseAPI } from "@shapr3d/frontend-lib";
import type { UseMutateAsyncFunction, UseMutationResult } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";

import { SearchParam } from "../types";
import { navigateToAuth } from "../utils/authUtils";
import { useMutateAcceptProjectInvitation, useProjectInvitation } from "./useProjectSharing";
import {
  type ProjectViewLinkGetInvitationResponseData,
  useMutateAcceptInvitation,
  useProjectViewLinkInvitation,
} from "./useProjectViewLink";

export enum InvitationType {
  viewlink = "viewlink",
  project = "project",
}

const useRemoveDanglingEmailToken = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.has(SearchParam.EmailToken) && !searchParams.has(SearchParam.InvitationHash)) {
      setSearchParams((search) => {
        search.delete(SearchParam.EmailToken);
        return search;
      });
    }
  }, [searchParams, setSearchParams]);
};

const useProcessEmailToken = (
  mutate: UseMutationResult<
    ResponseAPI | undefined,
    Error,
    | {
        emailToken?: string | null;
      }
    | undefined,
    unknown
  >
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { mutateAsync: mutateAcceptInvitation, isError, isIdle: isAcceptInvitationIdle } = mutate;

  useEffect(() => {
    const processEmailToken = async () => {
      try {
        await mutateAcceptInvitation({ emailToken: searchParams.get(SearchParam.EmailToken) });
        setSearchParams((search) => {
          search.delete(SearchParam.InvitationHash);
          search.delete(SearchParam.EmailToken);
          return search;
        });
      } catch (error) {
        if (
          error instanceof Error &&
          (error.message === "Missing refresh token" || error.message === HTTPStatus.Unauthorized)
        ) {
          setSearchParams((search) => {
            search.delete(SearchParam.EmailToken);
            return search;
          });
        }
      }
    };
    if (
      isAcceptInvitationIdle &&
      searchParams.has(SearchParam.EmailToken) &&
      searchParams.has(SearchParam.InvitationHash)
    ) {
      processEmailToken();
    }
  }, [searchParams, mutateAcceptInvitation, setSearchParams, isAcceptInvitationIdle]);

  return isError;
};

const useProcessInvitation = (
  invitationData: ProjectViewLinkGetInvitationResponseData | undefined,
  acceptInvitationMutate: UseMutateAsyncFunction<
    ResponseAPI | undefined,
    Error,
    | {
        emailToken?: string | null;
      }
    | undefined,
    unknown
  >
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const processInviteAction = async () => {
      if (invitationData?.action === "accept") {
        try {
          await acceptInvitationMutate({});
          setSearchParams((search) => {
            search.delete(SearchParam.InvitationHash);
            return search;
          });
        } catch (error) {
          if (
            error instanceof Error &&
            (error.message === "Missing refresh token" || error.message === HTTPStatus.Unauthorized)
          ) {
            setSearchParams((search) => {
              search.delete(SearchParam.InvitationHash);
              return search;
            });
          }
        }
      } else if (invitationData?.action === "view") {
        setSearchParams((search) => {
          search.delete(SearchParam.InvitationHash);
          return search;
        });
      } else if (invitationData?.action === "login" || invitationData?.action === "signup") {
        navigateToAuth({ authHint: invitationData?.action, emailHint: invitationData?.email });
      }
    };

    if (!searchParams.has(SearchParam.EmailToken) && searchParams.has(SearchParam.InvitationHash)) {
      processInviteAction();
    }
  }, [searchParams, invitationData, setSearchParams, acceptInvitationMutate]);
};

const useProcessProjectInvitationToken = () => {
  const [searchParams] = useSearchParams();
  const { data: invitationData, isError: isProjectError } = useProjectInvitation();
  const { mutateAsync: mutateAcceptInvitation, isError: isAcceptInvitationError } =
    useMutateAcceptProjectInvitation();

  const hasInvitationHash =
    !searchParams.has(SearchParam.EmailToken) && searchParams.has(SearchParam.InvitationHash);

  useProcessInvitation(invitationData, mutateAcceptInvitation);
  return { error: isAcceptInvitationError || isProjectError, hasInvitationHash };
};

const useProcessViewLinkInvitationToken = () => {
  const { data: invitationData, isError: isProjectViewLinkError } = useProjectViewLinkInvitation();
  const { mutateAsync: mutateAcceptInvitation, isError: isAcceptInvitationError } =
    useMutateAcceptInvitation();

  useProcessInvitation(invitationData, mutateAcceptInvitation);

  return isAcceptInvitationError || isProjectViewLinkError;
};

export const useHandleInvitation = () => {
  useRemoveDanglingEmailToken();
  const isEmailTokenError = useProcessEmailToken(useMutateAcceptInvitation());
  const isInvitationTokenError = useProcessViewLinkInvitationToken();
  return { isError: isInvitationTokenError || isEmailTokenError };
};

export const useHandleProjectInvitation = () => {
  useRemoveDanglingEmailToken();
  const isEmailTokenError = useProcessEmailToken(useMutateAcceptProjectInvitation());
  const { error: isInvitationTokenError, hasInvitationHash } = useProcessProjectInvitationToken();
  return { isError: isInvitationTokenError || isEmailTokenError, hasInvitationHash };
};
